import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDeployments } from '../../hooks/useDeployments';
import { useProviderKeys } from '../../hooks/useProviderKeys';
import { useSettings } from '../../hooks/useSettings';
import { useProviders } from '../../hooks/useProviders';
import { LoadingSpinner, EmptyState, ErrorState } from './UXStates';
import DeploymentRow from './DeploymentRow';
import './DeploymentActivityTable.css';

const POLL_INTERVAL_MS = 5000;

const DeploymentActivityTable = () => {
  const { getDeployments } = useDeployments();
  const { getProviderKeys } = useProviderKeys();
  const { getSubscriptions } = useSettings();
  const { providers, fetchProviders, addModel } = useProviders();
  const [deployments, setDeployments] = useState([]);
  const [providerKeys, setProviderKeys] = useState([]);
  const [hasReadyApi, setHasReadyApi] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollingRef = useRef(null);

  const fetchData = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const [deps, keys, subs, _] = await Promise.all([
        getDeployments(),
        getProviderKeys(),
        getSubscriptions(),
        fetchProviders()
      ]);
      setDeployments(deps);
      setProviderKeys(keys);
      
      // Check if user has an active "Ready API" subscription
      const readyApiSub = subs.find(s => 
        (s.status === 'active' || s.status === 'trialing') && 
        (s.agents?.name?.toLowerCase().includes('ready api') || s.agents?.id === 'ready-api')
      );
      setHasReadyApi(!!readyApiSub);
    } catch (err) {
      if (!isBackground) setError(err.message);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [getDeployments, getProviderKeys, getSubscriptions, fetchProviders]);

  useEffect(() => {
    fetchData();
    pollingRef.current = setInterval(() => fetchData(true), POLL_INTERVAL_MS);
    return () => clearInterval(pollingRef.current);
  }, [fetchData]);

  if (loading && deployments.length === 0) return <LoadingSpinner message="Locating active agents..." />;
  if (error) return <ErrorState message={error} onDismiss={() => setError(null)} />;
  if (deployments.length === 0) return (
    <EmptyState 
      icon="🛰️" 
      title="No active missions found" 
      description="Start an agent from the library to see it here." 
    />
  );

  return (
    <div className="activity-table-container">
      <div className="activity-table">
        <div className="table-header">
          <div className="header-cell">Name</div>
          <div className="header-cell">Provider</div>
          <div className="header-cell">Bundle</div>
          <div className="header-cell">Status</div>
          <div className="header-cell">Platform</div>
          <div className="header-cell action">Settings</div>
        </div>
        <div className="table-body">
          {deployments.map(deployment => (
            <DeploymentRow 
              key={deployment.id} 
              deployment={deployment} 
              providerKeys={providerKeys}
              providers={providers}
              addModelToDb={addModel}
              hasReadyApi={hasReadyApi}
              onUpdate={() => fetchData(true)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DeploymentActivityTable;
